import * as React from 'react';
import ReactSelect, {
	DropdownIndicatorProps,
	MultiValue,
	SingleValue,
	components,
} from 'react-select';
import { useFiltersContext } from '../../contexts/filters';
import { ITaxonomy } from '../../types';

interface IProps {
	taxonomy: ITaxonomy;
}

interface ISelectOption {
	label: string;
	value: string;
}

const Pills = ({ taxonomy }: IProps) => {
	let options = taxonomy.terms.map((term) => {
		// decode htmlentities.
		const label = document.createElement('textarea');
		label.innerHTML = term.name;

		return {
			label: label.value,
			value: '' + term.term_id,
		};
	});

	options = [{ label: 'All', value: '' }, ...options];

	const { selectedTax, selectTaxTerm, initSelectedTax } = useFiltersContext();
	const [value, setValue] = React.useState<ISelectOption[]>([options[0]]);
	const prevValue = React.useRef<string[]>([options[0].value]);
	const [placeholder, setPlaceholder] = React.useState<string>('');

	const handleOnChange = (newValue: SingleValue<ISelectOption> | MultiValue<ISelectOption>) => {
		let option = Array.isArray(newValue) ? newValue : [newValue].filter(Boolean); // Ensure we have an array.

		if (prevValue.current.length === 1 && prevValue.current.includes('')) {
			// If the previous value is an array with only an empty string, remove it.
			option = option.filter((opt) => opt.value !== '');
		} else if (option.length > 1 && option.some((opt) => opt.value === '')) {
			// If the new value is an array with an empty string, remove all other values.
			option = [options[0]];
		}

		if (option && selectTaxTerm) {
			const selectedValues = option.map((opt) => opt.value);

			if (JSON.stringify(selectedValues) !== JSON.stringify(prevValue.current)) {
				setValue(option);
				const termIds = selectedValues
					.filter((termId) => termId !== '') // Remove empty string.
					.map((termId) => +termId); // Convert to number.
				selectTaxTerm(taxonomy.name, termIds);

				if (window.dataLayer) {
					window.dataLayer.push({
						event: 'post_filter',
						filter: taxonomy.name,
						filter_selection: option.map((opt) => opt.label), // eslint-disable-line camelcase
					});
				}
			}

			prevValue.current = selectedValues;
		}
	};

	const handleCheckBoxChange = (option: ISelectOption) => {
		const newValue = value.some((val) => val.value === option.value)
			? value.filter((val) => val.value !== option.value)
			: [...value, option]; // Add the option to the array.

		handleOnChange(newValue);
	};

	React.useEffect(() => {
		// When filters are reset, set our value back to the first option.
		if (
			!Object.values(selectedTax).length ||
			(Object.values(initSelectedTax).length > 0 && initSelectedTax === selectedTax)
		) {
			setValue([options[0]]);
			prevValue.current = [options[0].value];
		}

		// Update the placeholder text.
		if (value.length === 0 || value.some((val) => val.value === '')) {
			setPlaceholder('All');
		} else {
			setPlaceholder(`${taxonomy.labels.name} (${value.length})`);
		}
	}, [selectedTax]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		if (initSelectedTax && Object.values(initSelectedTax).length > 0) {
			if (initSelectedTax && taxonomy && taxonomy.name) {
				const initValue = options.filter(
					(option) => option.value === String(initSelectedTax[taxonomy.name])
				);

				setValue(initValue);
			}
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const DropdownIndicator = (props: DropdownIndicatorProps<ISelectOption>) => {
		return (
			<components.DropdownIndicator {...props}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M12.0759 11.3709L17.1481 5.02588L18.7754 6.32673L13.7032 12.6717C12.8274 13.7672 11.501 14.405 10.0985 14.405C8.74755 14.405 7.46444 13.8131 6.58764 12.7853L1.09971 6.35236L2.68466 5.00024L8.17259 11.4332C8.65356 11.997 9.35743 12.3217 10.0985 12.3217C10.8679 12.3217 11.5955 11.9718 12.0759 11.3709Z"
						fill="#505050"
					/>
				</svg>
			</components.DropdownIndicator>
		);
	};

	return (
		<>
			<ReactSelect
				className="pfa-filter-select pfa-filter-select--mobile"
				classNamePrefix="pfa-filter-select"
				options={options}
				components={{ DropdownIndicator }}
				onChange={handleOnChange}
				value={value}
				key={taxonomy.name}
				isClearable={false}
				isSearchable={false}
				controlShouldRenderValue={false}
				hideSelectedOptions={false}
				placeholder={placeholder}
				isMulti
			/>
			<div className="pfa-filter-large-pills pfa-filter-large-pills--desktop">
				{options.map((option) => (
					<label
						key={option.value}
						className="pfa-filter-large-pills__label btn"
						htmlFor={`pfa-filter-large-pills-${taxonomy.name}-${option.value}`}
					>
						<input
							type="checkbox"
							id={`pfa-filter-large-pills-${taxonomy.name}-${option.value}`}
							value={option.value}
							className="pfa-filter-large-pills__input"
							checked={value.some((val) => val.value === option.value)}
							onChange={() => handleCheckBoxChange(option)}
						/>
						{option.label}
					</label>
				))}
			</div>
		</>
	);
};

Pills.displayName = 'Pills';

export default Pills;
